import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import {
  ApiError,
  completeNewPassword,
  login,
  type NewPasswordChallenge,
  type SignedInResult,
} from "../lib/api";

const inputClass =
  "w-full rounded-lg border border-adriatic/25 bg-white px-3.5 py-2.5 text-adriatic-deep placeholder:text-adriatic-mid/50 focus:border-acqua focus:ring-2 focus:ring-acqua/30 focus:outline-none disabled:opacity-60";

const buttonClass =
  "w-full rounded-full bg-coral px-4 py-2.5 font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50";

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) return error.message;
  return "Could not reach the sign-in service. Check your connection and try again.";
}

function goTo(result: SignedInResult) {
  // assign rather than replace: this page stays in history, so a visitor who
  // lands somewhere they did not expect can press back and read the form
  // again rather than being bounced forward.
  //
  // The URL is the one the API returned, which it validated against the
  // allowlist. The raw query parameter is never navigated to.
  window.location.assign(result.redirectTo);
}

/**
 * The form. One mutation per step, and no client-side validation beyond what
 * the browser does for free: the API validates the shape and answers every
 * failure identically, so a client-side check would only be a second set of
 * rules to keep in step with the server's.
 *
 * There are two steps because Cognito has two: accounts are created by an
 * administrator with a temporary password, and the first sign-in answers a
 * new-password challenge instead of a session. The switch between them is the
 * `challenge` state; everybody after their first sign-in never sees it.
 */
export function LoginForm({ redirectUri }: { redirectUri: string | null }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [newPassword, setNewPassword] = useState("");

  const signIn = useMutation({
    mutationFn: () => login({ email, password, redirectUri }),
    onSuccess: (outcome) => {
      if (outcome.challenge === "new_password_required") {
        setChallenge(outcome);
        return;
      }
      goTo(outcome);
    },
  });

  const finishChallenge = useMutation({
    mutationFn: () => {
      if (!challenge) throw new Error("no challenge in progress");
      return completeNewPassword({
        email: challenge.email,
        newPassword,
        session: challenge.session,
        redirectUri,
      });
    },
    onSuccess: goTo,
    onError: (error) => {
      // Cognito's challenge session lives for a few minutes. Once it has
      // expired the only way forward is the password step, so put the visitor
      // back there rather than leaving a form that can never succeed.
      if (error instanceof ApiError && error.code === "challenge_expired") {
        setChallenge(null);
        setNewPassword("");
      }
    },
  });

  if (challenge) {
    // The redirect happens after a successful mutation, and the browser takes
    // a moment over it. Staying disabled through `isSuccess` stops a second
    // submission in that window.
    const busy = finishChallenge.isPending || finishChallenge.isSuccess;
    const message = errorMessage(finishChallenge.error);

    function onSubmitNewPassword(event: FormEvent) {
      event.preventDefault();
      if (!busy) finishChallenge.mutate();
    }

    return (
      <form onSubmit={onSubmitNewPassword} className="space-y-4" noValidate>
        <p className="text-sm text-adriatic-mid">
          First sign-in: choose a password to replace the temporary one.
        </p>

        <div className="space-y-1.5">
          <label htmlFor="new-password" className="block text-sm font-medium text-adriatic-deep">
            New password
          </label>
          <input
            id="new-password"
            name="new-password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            // biome-ignore lint/a11y/noAutofocus: the one field on the step somebody was just sent to
            autoFocus
            required
            disabled={busy}
            className={inputClass}
          />
        </div>

        {message && (
          <p role="alert" aria-live="polite" className="text-sm text-coral-ink">
            {message}
          </p>
        )}

        <button type="submit" disabled={busy || newPassword.length === 0} className={buttonClass}>
          {busy ? "Setting password..." : "Set password and sign in"}
        </button>
      </form>
    );
  }

  const busy = signIn.isPending || signIn.isSuccess;
  const message = errorMessage(signIn.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!busy) signIn.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-sm font-medium text-adriatic-deep">
          Work email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          // Standard tokens, so a password manager recognises this as a
          // sign-in form and offers the credential it saved for the estate.
          autoComplete="username"
          // The a11y rule guards against focus being stolen from whatever a
          // visitor was reading. This page has nothing else on it: it is one
          // form, reached by being sent here, and the first thing anybody does
          // is type an email.
          // biome-ignore lint/a11y/noAutofocus: nothing else on the page to take focus from
          autoFocus
          required
          spellCheck={false}
          disabled={busy}
          placeholder="you@acquire.ai"
          className={inputClass}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="password" className="block text-sm font-medium text-adriatic-deep">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          disabled={busy}
          className={inputClass}
        />
      </div>

      {message && (
        // aria-live, so somebody using a screen reader hears the refusal. A
        // failed sign-in changes nothing else on the page, so without this the
        // form simply appears not to have responded.
        <p role="alert" aria-live="polite" className="text-sm text-coral-ink">
          {message}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || email.length === 0 || password.length === 0}
        className={buttonClass}
      >
        {busy ? "Signing in..." : "Sign in"}
      </button>

      <p className="pt-1 text-center text-xs text-adriatic-mid">
        One sign-in covers every AI Labs project.
      </p>
    </form>
  );
}
